import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Dashboard from './components/Dashboard';
import Challenges from './components/Challenges';
import PromptQuest from './components/PromptQuest';
import QuestLevel from './components/QuestLevel';
import Playground from './components/Playground';
import Reference from './components/Reference';
import Leaderboard from './components/Leaderboard';
import Profile from './components/Profile';
import AdminPanel from './components/AdminPanel';
import GamificationHub from './components/GamificationHub';
import HelpFAQ from './components/HelpFAQ';
import Lesson from './components/Lesson';
import Review from './components/Review';
import FrameworksCatalog from './components/FrameworksCatalog';
import ProductionCatalog from './components/ProductionCatalog';
import TrackLessonList from './components/TrackLessonList';
import AgentBetaChat from './components/layout/AgentBetaChat';
import AppShell from './components/layout/AppShell';
import Landing from './components/Landing';
import { ThemeProvider } from './contexts/ThemeContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';

import LucideIcon from './components/LucideIcon';
import UsernameBasedAuth from './components/UsernameBasedAuth';
import ForgotPassword from './components/ForgotPassword';
import OnboardingFlow from './components/onboarding/OnboardingFlow';
import Privacy from './components/legal/Privacy';
import Terms from './components/legal/Terms';
import DataRights from './components/legal/DataRights';
import { API } from './utils/api';

// Custom auth - no Amplify configuration needed

function LoadingScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-900">
      <div className="text-center">
        <LucideIcon name="logo" size={80} />
        <p className="text-white mt-4">Loading...</p>
      </div>
    </div>
  );
}

// Gates the authenticated app behind onboarding: fetches onboarding status
// once per authenticated session (not per route change -- this mounts once
// when isAuthenticated flips true and stays mounted across navigation
// within AuthenticatedApp) and shows OnboardingFlow until it's complete.
function AuthenticatedGate() {
  const [onboardingCompleted, setOnboardingCompleted] = useState<boolean | null>(null);
  // If the user already completed the Goal step (server has onboardingStep:
  // 'goal') and then refreshed or closed the tab before Placement, resume
  // there instead of silently restarting the whole funnel from the top.
  const [resumeStep, setResumeStep] = useState<'goal' | 'placement'>('goal');

  useEffect(() => {
    let cancelled = false;
    API.get('/progress')
      .then((response: any) => {
        if (!cancelled) {
          const data = response?.[0];
          setOnboardingCompleted(!!data?.onboardingCompleted);
          if (data?.onboardingStep === 'goal') {
            setResumeStep('placement');
          }
        }
      })
      .catch(() => {
        // If we can't tell, don't trap the user behind onboarding --
        // fail open into the normal app rather than fail closed.
        if (!cancelled) setOnboardingCompleted(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (onboardingCompleted === null) {
    return <LoadingScreen />;
  }

  if (!onboardingCompleted) {
    return <OnboardingFlow initialStep={resumeStep} onComplete={() => setOnboardingCompleted(true)} />;
  }

  return (
    <Routes>
      <Route path="/login" element={<Navigate to="/" replace />} />
      <Route path="/signup" element={<Navigate to="/" replace />} />
      <Route path="/forgot-password" element={<Navigate to="/" replace />} />
      {/* Legal documents render the same signed in or out, outside the app
          chrome -- someone reading a privacy policy shouldn't be looking at a
          Dashboard sidebar. Declared in both route trees so a footer link
          works either way. */}
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/data-rights" element={<DataRights />} />
      <Route path="/*" element={<AuthenticatedApp />} />
    </Routes>
  );
}

function AuthenticatedApp() {
  const { user, signOut } = useAuth();

  return (
    <AppShell user={user} signOut={signOut}>
      <Routes>
        <Route path="/" element={<Dashboard />} />
        <Route path="/challenges" element={<Challenges />} />
        <Route path="/lessons/:lessonId" element={<Lesson />} />
        <Route path="/review" element={<Review />} />
        <Route path="/frameworks" element={<FrameworksCatalog />} />
        <Route path="/production" element={<ProductionCatalog />} />
        <Route path="/tracks/:trackSlug" element={<TrackLessonList />} />
        <Route path="/prompt-quest" element={<PromptQuest />} />
        <Route path="/prompt-quest/:questId" element={<QuestLevel />} />
        <Route path="/playground" element={<Playground />} />
        <Route path="/reference" element={<Reference />} />
        <Route path="/gamification" element={<GamificationHub />} />
        <Route path="/leaderboard" element={<Leaderboard />} />
        <Route path="/help-faq" element={<HelpFAQ />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/admin" element={<AdminPanel />} />
        {/* Internal-only test surface for the Milestone-2 LangGraph agent
            (docs/build-plan/06-agent-framework-backend.md) -- not linked
            from anywhere in the app chrome, and only registered at all when
            REACT_APP_AGENT_BETA_ENABLED=true is set at build time. The
            backend route itself is a second, independent gate (an
            AGENT_CHAT_BETA_USERS allowlist), so this flag alone doesn't
            expose the feature to real users. */}
        {process.env.REACT_APP_AGENT_BETA_ENABLED === 'true' && (
          <Route path="/agent-beta" element={<AgentBetaChat />} />
        )}
        {/* Any other authenticated deep link falls back to the Dashboard. */}
        <Route path="*" element={<Dashboard />} />
      </Routes>
    </AppShell>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <AppContent />
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

function AppContent() {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return (
      <Routes>
        {/* Public access stays closed until an intentional launch build. */}
        <Route path="/login" element={process.env.REACT_APP_PUBLIC_ACCESS_ENABLED === 'true' ? <UsernameBasedAuth initialView="login" /> : <Navigate to="/" replace />} />
        <Route path="/signup" element={process.env.REACT_APP_PUBLIC_ACCESS_ENABLED === 'true' ? <UsernameBasedAuth initialView="signup" /> : <Navigate to="/" replace />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/data-rights" element={<DataRights />} />
        {/* Landing is the entry point for everyone else, including any
            deep link to a route that requires auth -- there is no
            "redirect back after login" flow (not asked for; keep it simple). */}
        <Route path="*" element={<Landing />} />
      </Routes>
    );
  }

  return <AuthenticatedGate />;
}

export default App;
